import { useState, useEffect } from 'react';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';

import axios from '../../../api/axios';
import {
    NAME_REGEX,
    EMAIL_REGEX,
    USER_REGEX,
    PWD_REGEX,
} from '../../utils/constants';

const RegisterForm = () => {
    const [firstName, setFirstName] = useState('');
    const [validFirstName, setValidFirstName] = useState(false);

    const [lastName, setLastName] = useState('');
    const [validLastName, setValidLastName] = useState(false);

    const [email, setEmail] = useState('');
    const [validEmail, setValidEmail] = useState(false);

    const [user, setUser] = useState('');
    const [validUser, setValidUser] = useState(false);

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);

    const [errMsg, setErrMsg] = useState('');
    const [requestSubmitted, setRequestSubmitted] = useState(false);

    useEffect(() => {
        setValidFirstName(NAME_REGEX.test(firstName));
        setValidLastName(NAME_REGEX.test(lastName));
        setValidEmail(EMAIL_REGEX.test(email));
        setValidUser(USER_REGEX.test(user));
        setValidPwd(PWD_REGEX.test(pwd));
        setValidMatch(pwd === matchPwd);
    }, [firstName, lastName, email, user, pwd, matchPwd]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (
            !validFirstName ||
            !validLastName ||
            !validEmail ||
            !validUser ||
            !validPwd ||
            !validMatch
        ) {
            setErrMsg('Please correct form fields and try again.');
            return;
        }

        try {
            const requestBody = {
                first_name: firstName,
                last_name: lastName,
                email: email,
                username: user,
                password: pwd,
            };
            const response = await axios.post(
                '/api/register/',
                JSON.stringify(requestBody),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                }
            );

            if (response.status === 201) {
                setRequestSubmitted(true);
                setErrMsg('');
            }
        } catch (err) {
            if (!err?.response) {
                setErrMsg('No Server Response');
            } else if (err.response?.status === 400) {
                const responseData = err.response?.data;
                const firstError = Object.values(responseData || {})
                    .flat()
                    .find(Boolean);
                setErrMsg(firstError || 'Please review your request details.');
            } else {
                setErrMsg('Request Failed');
            }
        }
    };

    if (requestSubmitted) {
        return (
            <section>
                <h1>Request received</h1>
                <p>
                    Thanks for your interest in Finspace. We will review your
                    request and follow up when your account is ready.
                </p>
                <p>
                    Already approved? <Link to="/login">Sign In</Link>
                </p>
            </section>
        );
    }

    return (
        <>
            <section>
                <h1>Request access</h1>
                <form onSubmit={handleSubmit}>
                    <p>
                        Tell us who you are and choose the login details you
                        would like to use if approved.
                    </p>
                    <div className="form-group mb-2">
                        <label htmlFor="firstName" className="mb-0">
                            Name
                        </label>
                        <div className="name-input">
                            <input
                                className="form-control w-50"
                                type="text"
                                placeholder="First"
                                id="firstName"
                                autoComplete="off"
                                onChange={(e) => setFirstName(e.target.value)}
                                value={firstName}
                                required
                                aria-invalid={validFirstName ? 'false' : 'true'}
                                aria-describedby="uidnote"
                                onBlur={() =>
                                    setValidFirstName(
                                        NAME_REGEX.test(firstName)
                                    )
                                }
                            />
                            <input
                                className="form-control w-50"
                                type="text"
                                placeholder="Last"
                                id="lastName"
                                autoComplete="off"
                                onChange={(e) => setLastName(e.target.value)}
                                value={lastName}
                                required
                                aria-invalid={validLastName ? 'false' : 'true'}
                                aria-describedby="uidnote"
                                onBlur={() =>
                                    setValidLastName(NAME_REGEX.test(lastName))
                                }
                            />
                        </div>
                    </div>
                    <div className="form-group mb-2">
                        <label htmlFor="email" className="mb-0">
                            Email Address
                        </label>
                        <input
                            className="form-control"
                            type="text"
                            placeholder="Email"
                            id="email"
                            autoComplete="off"
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                            required
                            aria-invalid={validEmail ? 'false' : 'true'}
                            aria-describedby="uidnote"
                            onBlur={() =>
                                setValidEmail(EMAIL_REGEX.test(email))
                            }
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label htmlFor="username" className="mb-0">
                            Username
                        </label>
                        <input
                            className="form-control"
                            type="text"
                            placeholder="Username"
                            id="username"
                            autoComplete="off"
                            onChange={(e) => setUser(e.target.value)}
                            value={user}
                            required
                            aria-invalid={validUser ? 'false' : 'true'}
                            aria-describedby="uidnote"
                            onBlur={() => setValidUser(USER_REGEX.test(user))}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label htmlFor="password" className="mb-0">
                            Password
                        </label>
                        <input
                            className="form-control"
                            type="password"
                            placeholder="Password"
                            id="password"
                            onChange={(e) => setPwd(e.target.value)}
                            value={pwd}
                            required
                            aria-invalid={validPwd ? 'false' : 'true'}
                            aria-describedby="pwdnote"
                            onBlur={() => setValidPwd(PWD_REGEX.test(pwd))}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label htmlFor="confirm_pwd" className="mb-0">
                            Confirm Password
                        </label>
                        <input
                            className="form-control"
                            type="password"
                            placeholder="Password Confirmation"
                            id="confirm_pwd"
                            onChange={(e) => setMatchPwd(e.target.value)}
                            value={matchPwd}
                            required
                            aria-invalid={validMatch ? 'false' : 'true'}
                            aria-describedby="confirmnote"
                            onBlur={() => setValidMatch(pwd === matchPwd)}
                        />
                    </div>
                    <div className="submit-button">
                        <button type="submit" className="btn button-secondary">
                            Request Access
                        </button>
                    </div>
                    <p>
                        Already approved? <Link to="/login">Sign In</Link>
                    </p>
                </form>
                {firstName && !validFirstName && (
                    <p id="uidnote" className={'instructions'}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        Must be a valid first name.
                    </p>
                )}
                {lastName && !validLastName && (
                    <p id="uidnote" className={'instructions'}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        Must be a valid last name.
                    </p>
                )}
                {email && !validEmail && (
                    <p id="uidnote" className={'instructions'}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        Must be a valid email address.
                    </p>
                )}
                {user && !validUser && (
                    <p id="uidnote" className={'instructions'}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        4 to 24 characters.
                        <br />
                        Must begin with a letter.
                        <br />
                        Letters, numbers, underscores, hyphens allowed.
                    </p>
                )}
                {pwd && !validPwd && (
                    <p id="uidnote" className={'instructions'}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        8 to 24 characters.
                        <br />
                        Must include uppercase and lowercase letters, a number
                        and a special character.
                        <br />
                        Allowed special characters:{' '}
                        <span aria-label="exclamation mark">!</span>{' '}
                        <span aria-label="at symbol">@</span>{' '}
                        <span aria-label="hashtag">#</span>{' '}
                        <span aria-label="dollar sign">$</span>{' '}
                        <span aria-label="percent">%</span>
                    </p>
                )}
                {matchPwd && !validMatch && (
                    <p id="uidnote" className={'instructions'}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        Must match the first password input field.
                    </p>
                )}
                {errMsg && (
                    <p id="uidnote" className={'errmsg'} aria-live="assertive">
                        {errMsg}
                    </p>
                )}
            </section>
        </>
    );
};

export default RegisterForm;
